import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { Fragment, Slice } from "@tiptap/pm/model";
import { PluginKey } from "@tiptap/pm/state";
import { dropPoint } from "@tiptap/pm/transform";
import { EditorView } from "@tiptap/pm/view";
import type { Node as PMNode } from "prosemirror-model";

export interface MediaSetDragState {
  mediaSetId: string | null;
  dropPos: number | null;
  slotHeight: number;
}

export type MediaSetDragMeta =
  | { type: "start"; mediaSetId: string; slotHeight: number }
  | { type: "target"; dropPos: number | null }
  | { type: "clear" };

export const mediaSetDragPluginKey = new PluginKey<MediaSetDragState>("mediaSetDrag");

export function mediaSetSlice(node: PMNode) {
  return new Slice(Fragment.from(node), 0, 0);
}

export function findMediaSet(doc: PMNode, nodeName: string, mediaSetId: string) {
  let match: { node: PMNode; pos: number } | undefined;
  doc.descendants((node, pos) => {
    if (node.type.name === nodeName && node.attrs.id === mediaSetId) {
      match = { node, pos };
      return false;
    }
    return true;
  });
  return match;
}

/** `undefined` means the pointer is outside the editor; `null` means over the dragged set. */
export function resolveMediaSetDropPosition(
  view: EditorView,
  containerId: string,
  left: number,
  top: number,
): number | null | undefined {
  const editorRect = view.dom.getBoundingClientRect();
  if (left < editorRect.left || left > editorRect.right || top < editorRect.top || top > editorRect.bottom) {
    return undefined;
  }

  const dragState = mediaSetDragPluginKey.getState(view.state);
  if (dragState?.mediaSetId !== containerId) return undefined;

  const activeSlotRect = view.dom.querySelector<HTMLElement>(".media-set-drop-slot")?.getBoundingClientRect();
  if (
    activeSlotRect &&
    left >= activeSlotRect.left &&
    left <= activeSlotRect.right &&
    top >= activeSlotRect.top &&
    top <= activeSlotRect.bottom
  ) {
    return dragState.dropPos;
  }

  const source = findMediaSet(view.state.doc, EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER, containerId);
  const coordinates = view.posAtCoords({ left, top });
  if (!source || !coordinates) return undefined;

  const candidate = dropPoint(view.state.doc, coordinates.pos, mediaSetSlice(source.node));
  const overSource = candidate !== null && candidate >= source.pos && candidate <= source.pos + source.node.nodeSize;
  return overSource ? null : candidate;
}
